//#region IMPORTS
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import classNames from "../../helpers/classNames";
//#endregion

// Tab identifiers for the projects page. Local state only — deliberately not in
// the URL (unlike /orders' `?tab=`), so these values are never linked to.
export const PROMO_TAB = {
  promo: "promo",
  nonPromo: "non-promo",
} as const;

export type PromoTab = (typeof PROMO_TAB)[keyof typeof PROMO_TAB];

const TAB_LABELS: Record<PromoTab, string> = {
  [PROMO_TAB.promo]: "rituals.promo",
  [PROMO_TAB.nonPromo]: "rituals.NONpromo",
};

const TOOLTIP_ID = "promos-non-promo-tab";

//#region INTERFACE
interface Props {
  activeTab: PromoTab;
  selectHandler: (tab: PromoTab) => void;
  nonPromoDisabled: boolean;
}
//#endregion

export default function PromosTabs({
  activeTab,
  selectHandler,
  nonPromoDisabled,
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();
  //#endregion

  //#region LOGIC
  // Same look as the /orders tabs (OrdersTabButton), minus the count badges —
  // those are order-specific.
  const tabClasses = (active: boolean, disabled: boolean) =>
    classNames(
      "relative px-4 py-3 text-sm font-medium border-b-2 -mb-px transition inline-flex items-center gap-2",
      disabled
        ? "border-transparent text-gray-300 cursor-not-allowed"
        : active
          ? "border-black text-black"
          : "border-transparent text-gray-500 hover:text-gray-800"
    );
  //#endregion

  //#region RENDER
  return (
    // No top padding: the nav sits bottom-aligned inside the fixed-height
    // MainStickyTabsBar, which owns the spacing.
    <nav className="flex gap-1 border-b border-gray-200" aria-label="Tabs">
      <button
        type="button"
        onClick={() => selectHandler(PROMO_TAB.promo)}
        className={tabClasses(activeTab === PROMO_TAB.promo, false)}
      >
        {t(TAB_LABELS[PROMO_TAB.promo])}
      </button>

      {/* `aria-disabled` + a guarded onClick instead of the `disabled`
          attribute: a truly disabled button fires no mouse events, so
          react-tooltip would never get to show the "not available" hint. */}
      <button
        type="button"
        aria-disabled={nonPromoDisabled}
        onClick={() =>
          !nonPromoDisabled && selectHandler(PROMO_TAB.nonPromo)
        }
        className={tabClasses(
          activeTab === PROMO_TAB.nonPromo,
          nonPromoDisabled
        )}
        {...(nonPromoDisabled
          ? {
              "data-tooltip-id": TOOLTIP_ID,
              "data-tooltip-content": t("rituals.notAvailable"),
              "data-tooltip-place": "bottom",
            }
          : {})}
      >
        {t(TAB_LABELS[PROMO_TAB.nonPromo])}
      </button>
      <Tooltip id={TOOLTIP_ID} />
    </nav>
  );
  //#endregion
}
